import { INTEGRATED_SOCIAL_ORIGIN, resolveSocialPreviewImage } from "@/lib/socialPreview";

/**
 * Social crawlers only see the document head. These durable 1200×630 assets are
 * used when a public page does not provide its own page-specific hero image.
 * They deliberately avoid the site mark/default card so a URL can earn a
 * meaningful preview in search, messaging and social surfaces.
 */
const MEDIA = (path: string) => `${INTEGRATED_SOCIAL_ORIGIN}${path}`;

export const GENERIC_SOCIAL_PREVIEW = MEDIA("/media/integrated-social-default-social-preview_94dfe470.jpg");

const PAGE_SOCIAL_PREVIEWS: Record<string, string> = {
  "/": MEDIA("/media/ai-marketing-persona-paid-earned-hero_193cca4c.jpg"),
  "/blog": MEDIA("/media/ai-visibility-benchmark-2026-citation-share-voice-hero-opt_2b933029.jpg"),
  "/ai-marketing-news": MEDIA("/media/openai-testflight-impersonation-advertiser-security-alert-2026-hero-web_0e0ab0cc.jpg"),
  "/services": MEDIA("/media/ai-marketing-persona-paid-earned-hero_193cca4c.jpg"),
  "/resources/ai-tools": MEDIA("/media/free-ai-marketing-tools-og-1200x630_205d9ae9.jpg"),
  "/tools": MEDIA("/media/free-ai-marketing-tools-og-1200x630_205d9ae9.jpg"),
  "/free-ai-growth-audit": MEDIA("/media/ai-visibility-check-vs-vanity-score-uk-b2b-hero-web_9cf5a39b.jpg"),
  "/free-ai-growth-audit/thank-you": MEDIA("/media/ai-visibility-check-vs-vanity-score-uk-b2b-hero-web_9cf5a39b.jpg"),
  "/pricing/": MEDIA("/media/ai-visibility-check-vs-vanity-score-uk-b2b-hero-web_9cf5a39b.jpg"),
  "/pricing": MEDIA("/media/aeo-agency-pricing-uk-cost-roi-guide-2026-hero_1e6615b8.png"),
  "/start": MEDIA("/media/ai-visibility-check-vs-vanity-score-uk-b2b-hero-web_9cf5a39b.jpg"),
  "/contact": MEDIA("/media/ai-marketing-persona-paid-earned-hero_193cca4c.jpg"),
  "/faq": MEDIA("/media/aeo-agency-uk-buyer-guide_32ffb356.jpg"),
  "/methodology": MEDIA("/media/iso-gtm-strategy_1b5eacd1.png"),
  "/editorial-policy": MEDIA("/media/transparent-aeo-evidence-scorecard-2026-hero_8212de93.jpg"),
  "/privacy-policy": MEDIA("/media/claude-mythos-ai-cybersecurity-banking-threat-2026-hero_a5e649c7.png"),
  "/cookie-policy": MEDIA("/media/claude-mythos-ai-cybersecurity-banking-threat-2026-hero_a5e649c7.png"),
  "/case-studies": MEDIA("/media/iso-gtm-strategy_1b5eacd1.png"),
  "/ai-marketing-agency-london": MEDIA("/media/iso-gtm-strategy_1b5eacd1.png"),
  "/ai-website-agency-london": MEDIA("/media/ai-websites-service-hero_dfe5e872.png"),
  "/marketing-agency-wimbledon": MEDIA("/media/iso-gtm-strategy_1b5eacd1.png"),
  "/services/ai-marketing-agency-london": MEDIA("/media/iso-gtm-strategy_1b5eacd1.png"),
  "/services/aeo-agency-london": MEDIA("/media/iso-seo-aeo_5a8d7cf0.png"),
  "/services/geo-agency-london": MEDIA("/media/iso-seo-aeo_5a8d7cf0.png"),
};

function normaliseRoute(routeOrCanonical: string): string {
  try {
    const parsed = new URL(routeOrCanonical, INTEGRATED_SOCIAL_ORIGIN);
    const pathname = parsed.pathname.replace(/\/+$/, "") || "/";
    return pathname;
  } catch {
    return routeOrCanonical.replace(/[?#].*$/, "").replace(/\/+$/, "") || "/";
  }
}

function isGenericOrLogoPreview(url: string): boolean {
  return /integrated-social-default-social-preview|(?:^|[-_/])logo(?:[-_.]|$)|\/og-image\.jpg(?:$|\?)/i.test(url);
}

function categoryPreview(pathname: string): string | undefined {
  const localisedPath = pathname.startsWith("/ar/") ? pathname.slice(3) || "/" : pathname;

  if (localisedPath.startsWith("/blog")) return PAGE_SOCIAL_PREVIEWS["/blog"];
  if (localisedPath.startsWith("/ai-marketing-news")) return PAGE_SOCIAL_PREVIEWS["/ai-marketing-news"];
  if (localisedPath.startsWith("/tools") || localisedPath.startsWith("/resources/ai-tools")) return PAGE_SOCIAL_PREVIEWS["/tools"];
  if (localisedPath.startsWith("/case-studies")) return PAGE_SOCIAL_PREVIEWS["/case-studies"];
  if (localisedPath.startsWith("/local/")) return MEDIA("/media/iso-gtm-strategy_1b5eacd1.png");
  if (localisedPath.includes("governance") || localisedPath.includes("compliance")) return MEDIA("/media/claude-mythos-ai-cybersecurity-banking-threat-2026-hero_a5e649c7.png");
  if (localisedPath.includes("agentic") || localisedPath.includes("gemini-agentic")) return MEDIA("/media/iso-agentic-service_40547b1b.png");
  if (localisedPath.includes("seo-aeo-geo") || localisedPath.includes("aeo") || localisedPath.includes("geo")) return MEDIA("/media/iso-seo-aeo_5a8d7cf0.png");
  if (localisedPath.includes("ppc") || localisedPath.includes("performance-max") || localisedPath.includes("google-ads")) return MEDIA("/media/iso-ppc-testing_ec9365bd.png");
  if (localisedPath.includes("website")) return MEDIA("/media/ai-websites-service-hero_dfe5e872.png");
  if (localisedPath.startsWith("/services")) return PAGE_SOCIAL_PREVIEWS["/services"];
  if (localisedPath === "/") return PAGE_SOCIAL_PREVIEWS["/"];
  return undefined;
}

/**
 * Returns a durable, absolute, route-relevant social image. A visible page hero
 * always wins, unless it is the generic social card or a logo masquerading as a
 * preview. Static hubs, service pages and public filters then receive a
 * semantic route fallback rather than the brand mark.
 */
export function resolveRouteSocialPreview(routeOrCanonical: string, suppliedImage?: string | null): string | undefined {
  const pathname = normaliseRoute(routeOrCanonical);
  const exact = PAGE_SOCIAL_PREVIEWS[pathname] ?? PAGE_SOCIAL_PREVIEWS[pathname.replace(/^\/ar(?=\/|$)/, "") || "/"];
  if (exact) return exact;

  const resolved = resolveSocialPreviewImage(suppliedImage);
  if (resolved && !isGenericOrLogoPreview(resolved)) return resolved;

  return categoryPreview(pathname) ?? resolved;
}

export function isGenericOrLogoSocialPreview(url?: string | null): boolean {
  return Boolean(url && isGenericOrLogoPreview(url));
}
